import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { HubConnectionState } from "@microsoft/signalr";
import { DocumentMoveJobFileResponse, DocumentMoveJobResponse, useDocumentService } from "../../services/documentService";
import { useDocumentMoveSignalR } from "../../hooks/useDocumentMoveSignalR";
import { useSnackbar } from "../../contexts/snackbar-context";

const formatStatus = (status: string): string => {
  if (status === "InProgress") return "In Progress";
  if (status === "CompletedWithErrors") return "Completed with Errors";
  return status;
};

const getFileStatusColor = (
  status: string
): "default" | "success" | "error" | "warning" | "info" => {
  switch (status) {
    case "Completed":
      return "success";
    case "Failed":
    case "OrphanedSource":
    case "OrphanedTarget":
      return "error";
    case "InProgress":
    case "BlobMoved":
      return "warning";
    case "NotStarted":
      return "info";
    default:
      return "default";
  }
};

const formatFileStatus = (status: string): string => {
  switch (status) {
    case "NotStarted": return "Not Started";
    case "InProgress": return "In Progress";
    case "BlobMoved": return "Blob Moved";
    case "OrphanedSource": return "Orphaned Source";
    case "OrphanedTarget": return "Orphaned Target";
    case "CompletedWithErrors": return "Completed With Errors";
    default: return status;
  }
};

const statusColor = (
  status: string
): "default" | "success" | "error" | "warning" | "info" => {
  if (status === "Completed") return "success";
  if (status === "Failed") return "error";
  if (status === "CompletedWithErrors") return "warning";
  if (status === "InProgress") return "warning";
  return "info";
};

const formatDateTime = (value?: string): string => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const isRetryable = (job: DocumentMoveJobResponse): boolean =>
  job.status === "Failed"
  || job.status === "CompletedWithErrors"
  || job.status === "Queued"
  || (job.status === "InProgress" && job.completedFileCount === 0);

const isActive = (status: string): boolean =>
  status === "Queued" || status === "InProgress";

const getPollingIntervalMs = (isConnected: boolean, jobs: DocumentMoveJobResponse[]): number => {
  const hasActiveJobs = jobs.some((x) => isActive(x.status));

  if (isConnected) {
    return hasActiveJobs ? 30000 : 0;
  }

  return hasActiveJobs ? 15000 : 45000;
};

const getRealtimeLabel = (
  isConnected: boolean,
  connectionState: HubConnectionState
): string => {
  if (isConnected) {
    return "Connected";
  }

  if (connectionState === HubConnectionState.Reconnecting) {
    return "Reconnecting";
  }

  return "Disconnected (fallback refresh active)";
};

const getSourceLabel = (job: DocumentMoveJobResponse): string => {
  return job.sourceFolderName || "Multiple files";
};

const getJobTypeLabel = (job: DocumentMoveJobResponse): string => {
  if (job.hasFileMoves && job.hasFolderMoves) return "Files+Folders";
  if (job.hasFolderMoves) return "Folders";
  return "Files";
};

const getJobTypeColor = (job: DocumentMoveJobResponse): string => {
  if (job.hasFileMoves && job.hasFolderMoves) return "#9c27b0";
  if (job.hasFolderMoves) return "#ff9800";
  return "#2196f3";
};

const getJobContext = (job: DocumentMoveJobResponse): string => {
  if (job.tailNumber) return job.tailNumber;
  if (job.assemblyType && job.assemblyFolder) {
    return `${job.assemblyType} ${job.assemblyFolder}`;
  }
  if (job.assemblyType) return job.assemblyType;
  return "-";
};

const DocumentMoveJobsPage: React.FC = () => {
  const { listMoveJobs, getMoveJob, retryMoveJob } = useDocumentService();
  const { showError, showSuccess } = useSnackbar();
  const listJobsRef = useRef(listMoveJobs);

  const [jobs, setJobs] = useState<DocumentMoveJobResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "queued" | "running" | "completed" | "failed"
  >("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [expandedJobFiles, setExpandedJobFiles] = useState<
    Record<string, DocumentMoveJobFileResponse[]>
  >({});
  const [loadingJobId, setLoadingJobId] = useState<string | null>(null);

  useEffect(() => {
    listJobsRef.current = listMoveJobs;
  }, [listMoveJobs]);

  const upsertJob = useCallback((incoming: DocumentMoveJobResponse) => {
    setJobs((previous) => {
      const index = previous.findIndex((x) => x.jobId === incoming.jobId);
      const next = [...previous];
      if (index >= 0) {
        next[index] = { ...next[index], ...incoming };
      } else {
        next.unshift(incoming);
      }

      next.sort((a, b) => {
        const aTicks = new Date(a.createdAt || 0).getTime();
        const bTicks = new Date(b.createdAt || 0).getTime();
        return bTicks - aTicks;
      });

      return next;
    });
  }, []);

  const refreshJobs = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);

    try {
      const result = await listJobsRef.current(100);
      setJobs(result);
    } catch (error) {
      showError(`Failed to load move jobs: ${(error as Error).message}`);
    } finally {
      if (showSpinner) {
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
    }
  }, [showError]);

  const handleConnectionError = useCallback((message: string) => {
    showError(message);
  }, [showError]);

  const { isConnected, connectionState } = useDocumentMoveSignalR({
    onJobUpdated: upsertJob,
    onConnectionError: handleConnectionError,
  });

  useEffect(() => {
    refreshJobs(true);
  }, [refreshJobs]);

  useEffect(() => {
    const intervalMs = getPollingIntervalMs(isConnected, jobs);

    if (!intervalMs) {
      return;
    }

    const pollId = window.setInterval(() => {
      refreshJobs(false);
    }, intervalMs);

    return () => window.clearInterval(pollId);
  }, [isConnected, jobs, refreshJobs]);

  useEffect(() => {
    if (isConnected) {
      refreshJobs(false);
    }
  }, [isConnected, refreshJobs]);

  const inProgressCount = useMemo(
    () => jobs.filter((x) => x.status === "InProgress").length,
    [jobs]
  );

  const queuedCount = useMemo(
    () => jobs.filter((x) => x.status === "Queued").length,
    [jobs]
  );

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      return statusFilter === "all"
        || (statusFilter === "queued" && job.status === "Queued")
        || (statusFilter === "running" && job.status === "InProgress")
        || (statusFilter === "completed" && (job.status === "Completed" || job.status === "CompletedWithErrors"))
        || (statusFilter === "failed" && job.status === "Failed");
    });
  }, [jobs, statusFilter]);

  const pagedJobs = useMemo(() => {
    const start = page * pageSize;
    return filteredJobs.slice(start, start + pageSize);
  }, [filteredJobs, page, pageSize]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredJobs.length / pageSize) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredJobs, page, pageSize]);

  const handleToggleExpand = useCallback(async (jobId: string) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      return;
    }

    setExpandedJobId(jobId);

    if (expandedJobFiles[jobId]) {
      return;
    }

    setLoadingJobId(jobId);
    try {
      const detail = await getMoveJob(jobId);
      if (detail) {
        upsertJob(detail);
      }
      setExpandedJobFiles((prev) => ({
        ...prev,
        [jobId]: detail?.files ?? [],
      }));
    } catch (error) {
      setExpandedJobFiles((prev) => ({
        ...prev,
        [jobId]: [],
      }));
      showError(`Failed to load move job details: ${(error as Error).message}`);
    } finally {
      setLoadingJobId(null);
    }
  }, [expandedJobFiles, expandedJobId, getMoveJob, showError, upsertJob]);

  const renderFileRows = (files: DocumentMoveJobFileResponse[] | undefined) => {
    if (!files?.length) {
      return (
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
          No file details available.
        </Typography>
      );
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderBottom: "none",
                padding: "6px 8px",
                flex: 2,
              }}
            >
              File
            </TableCell>
            <TableCell
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderBottom: "none",
                padding: "6px 8px",
                width: "140px",
              }}
            >
              Status
            </TableCell>
            <TableCell
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderBottom: "none",
                padding: "6px 8px",
                flex: 3,
              }}
            >
              Failure
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map((file) => {
            return (
              <TableRow key={file.fileId}>
                <TableCell
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "0.8rem",
                    padding: "6px 8px",
                    verticalAlign: "middle",
                    fontWeight: 500,
                    flex: 2,
                  }}
                >
                  {file.wasRenamed ? (
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "#fff",
                        }}
                      >
                        {file.renamedToFileName}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "rgba(255,255,255,0.5)",
                          fontStyle: "italic",
                        }}
                      >
                        Renamed from: {file.originalFileName}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 500 }}>
                      {file.fileName}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    width: "140px",
                    fontSize: "0.8rem",
                    padding: "6px 8px",
                    verticalAlign: "middle",
                  }}
                >
                  <Chip
                    size="small"
                    label={formatFileStatus(file.status)}
                    color={getFileStatusColor(file.status)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: "0.8rem",
                    padding: "6px 8px",
                    verticalAlign: "middle",
                    flex: 3,
                  }}
                >
                  {file.failureReason || "-"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
            Move Jobs
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
            Track file and folder move operations.
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block", mt: 0.5 }}>
            In progress: {inProgressCount}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block" }}>
            Queued: {queuedCount}
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block" }}>
            Realtime: {getRealtimeLabel(isConnected, connectionState)}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => refreshJobs(true)}
          disabled={isLoading}
          sx={{
            color: "hsl(19 101% 51% / 1)",
            borderColor: "hsl(19 101% 51% / 1)",
            textTransform: "none",
            fontWeight: 700,
          }}
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ backgroundColor: "#111", border: "1px solid rgba(255,255,255,0.2)" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid rgba(255,255,255,0.14)" }}
        >
          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
            <InputLabel sx={{ color: "rgba(255,255,255,0.8)" }}>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(event) => setStatusFilter(event.target.value as "all" | "queued" | "running" | "completed" | "failed")}
              sx={{ color: "#fff" }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="queued">Queued</MenuItem>
              <MenuItem value="running">Running</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ position: "relative" }}>
          {hasLoadedOnce && isLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(0,0,0,0.45)",
                borderRadius: 1,
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} sx={{ color: "hsl(19 101% 51% / 1)" }} />
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                  Refreshing...
                </Typography>
              </Stack>
            </Box>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "80px" }}>Type</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "180px" }}>Source</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "200px" }}>Destination</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "100px" }}>Context</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "130px" }}>Status</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "90px" }}>Progress</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "160px" }}>Started by</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "150px" }}>Created</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, width: "150px" }}>Completed</TableCell>
                <TableCell sx={{ color: "#fff", fontWeight: 700, minWidth: 200 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!hasLoadedOnce && isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", py: 3 }}>
                    <Stack direction="row" spacing={1.5} justifyContent="center" alignItems="center">
                      <CircularProgress size={18} sx={{ color: "hsl(19 101% 51% / 1)" }} />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                        Loading move jobs...
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : filteredJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} sx={{ color: "rgba(255,255,255,0.8)", textAlign: "center", py: 3 }}>
                    No move jobs match your filters.
                  </TableCell>
                </TableRow>
              ) : (
                pagedJobs.map((job) => {
                  const expanded = expandedJobId === job.jobId;
                  return (
                    <React.Fragment key={job.jobId}>
                      <TableRow hover>
                        <TableCell>
                          <Chip
                            label={getJobTypeLabel(job)}
                            size="small"
                            variant="outlined"
                            sx={{
                              color: getJobTypeColor(job),
                              borderColor: getJobTypeColor(job),
                              fontSize: "0.7rem",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {getSourceLabel(job)}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {job.targetFolderName}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {getJobContext(job)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatStatus(job.status)}
                            color={statusColor(job.status)}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {job.completedFileCount} / {job.totalFileCount}
                        </TableCell>
                        <TableCell
                          sx={{
                            color: "#fff",
                            wordBreak: "break-all",
                            maxWidth: 160,
                            fontSize: "0.8rem",
                          }}
                        >
                          {job.initiatedBy}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {formatDateTime(job.createdAt)}
                        </TableCell>
                        <TableCell sx={{ color: "#fff" }}>
                          {formatDateTime(job.completedAt)}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 0.5,
                              alignItems: "center",
                              flexWrap: "nowrap",
                            }}
                          >
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => void handleToggleExpand(job.jobId)}
                              sx={{
                                color: "hsl(19 101% 51% / 1)",
                                borderColor: "hsl(19 101% 51% / 1)",
                                textTransform: "none",
                                whiteSpace: "nowrap",
                              }}
                            >
                              View details
                            </Button>
                            {isRetryable(job) && (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<ReplayIcon />}
                                onClick={async () => {
                                  const retried = await retryMoveJob(job.jobId);
                                  if (!retried) {
                                    showError("Unable to retry move job.");
                                    return;
                                  }
                                  upsertJob(retried);
                                  showSuccess("Move job moved back to queue.");
                                }}
                                sx={{
                                  color: "hsl(19 101% 51% / 1)",
                                  borderColor: "hsl(19 101% 51% / 1)",
                                  textTransform: "none",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Retry
                              </Button>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={10}
                          sx={{
                            py: 0,
                            backgroundColor: "rgba(255,255,255,0.03)",
                            borderBottom: expanded ? undefined : 0,
                          }}
                        >
                          <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                px: 3,
                                py: 1.5,
                                backgroundColor: "rgba(255,255,255,0.03)",
                                borderLeft: "3px solid rgba(255,255,255,0.1)",
                                marginLeft: "8px",
                              }}
                            >
                              {loadingJobId === job.jobId ? (
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                  <CircularProgress size={18} sx={{ color: "hsl(19 101% 51% / 1)" }} />
                                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
                                    Loading file details...
                                  </Typography>
                                </Stack>
                              ) : (
                                renderFileRows(expandedJobFiles[job.jobId])
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={filteredJobs.length}
          page={page}
          onPageChange={(_event, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50]}
          onRowsPerPageChange={(event) => {
            setPageSize(Number.parseInt(event.target.value, 10));
            setPage(0);
          }}
          sx={{ color: "#fff", borderTop: "1px solid rgba(255,255,255,0.14)" }}
        />
      </TableContainer>
    </Box>
  );
};

export default DocumentMoveJobsPage;
